// @flow

/* eslint-disable-next-line no-unused-vars */
import React, { useEffect } from 'react';

import TrialCloseButton from '@zapier/design-system-company/src/components/TrialCloseButton';
import TrialWelcomeModalHeader from '@zapier/design-system-company/src/components/TrialWelcomeModalHeader';
import { Modal } from '@zapier/common-components';
import {
  Button,
  Colors,
  Heading,
  HeadingLevel,
  LoadableImg,
  Margin,
  Spacer,
  Text,
  Typography,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/common-utils';
import { useTrackEvent, useTrackingContext } from '@zapier/toolbox-tracking';

import { type Size } from '@zapier/design-system-company/src/types';

/** @jsx isolateComponent */

type Props = {
  onClose: () => void,
  size: Size,
  startHref: string,
};

const Styles = labelStyles(__filename, {
  buttonWrapper: {
    textAlign: 'center',
  },
  heading: {
    ...Typography.subtitle900,
    color: Colors.grey900,
    textAlign: 'center',
  },
  subHeading: {
    ...Typography.subtitle800,
    color: Colors.grey900,
    textAlign: 'center',
  },
  warning: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
});

const TrialConfirmationModal = (props: Props) => {
  const { trackEvent } = useTrackingContext();

  useTrackEvent('company-trial-confirmation-shown');

  const handleClose = () => {
    trackEvent('company-trial-confirmation-closed');
    props.onClose();
  };

  return (
    <Modal
      closeOnOutsideClick={true}
      maxWidth={600}
      onClose={handleClose}
      overflowX="visible"
      overflowY="auto"
      padding={0}
      renderCloseButton={() => <TrialCloseButton onClose={handleClose} />}
    >
      <TrialWelcomeModalHeader trimChildren={true}>
        <LoadableImg
          alt="Company features"
          ariaHidden={true}
          aspectRatio="600:206"
          hasRadius={false}
          src="https://zapier-images.imgix.net/storage/photos/4b71389ffdabaa0f5f50eae287423b12.png"
        />
      </TrialWelcomeModalHeader>

      <Margin margin="25px 40px 40px 40px">
        <Heading css={Styles.heading}>Welcome to Zapier for Companies</Heading>

        <HeadingLevel>
          <Spacer height={10} />

          <Text color="grey900" tag="p" textAlign="center" type="bodyText900">
            Try our advanced administrative and security controls to bring
            automation to your entire organization.
          </Text>

          <Spacer height={30} />

          <Heading css={Styles.subHeading}>Ready to start your trial?</Heading>

          <Spacer height={5} />

          <Text color="grey900" tag="p" textAlign="center" type="altText800">
            Task limits do not change from your current plan during the trial
          </Text>

          <Spacer height={21} />

          <div css={Styles.buttonWrapper}>
            <Button
              casing="uppercase"
              color="black"
              hasShadow={true}
              href={props.startHref}
              isFullWidth={props.size === 'small'}
              onClick={() => {
                trackEvent('company-trial-confirmation-start-click');
              }}
            >
              Get Started
            </Button>
          </div>
        </HeadingLevel>
      </Margin>
    </Modal>
  );
};

export default TrialConfirmationModal;
